import {
  detectBrowser,
  detectBrowserVersion,
  detectDeviceType,
  detectOS,
} from '@posthog/core/utils';
import {
  PRODUCT_INSIGHT_APP_ID,
  PRODUCT_INSIGHT_PROJECT,
  PRODUCT_INSIGHT_SCHEMA_VERSION,
  PRODUCT_INSIGHT_SDK,
  PRODUCT_INSIGHT_SDK_VERSION,
  type ProductInsightApplication,
  type ProductInsightEvent,
} from '@toggle/toggle';
import { v4 } from 'uuid';

interface CreateProductInsightEventParams {
  eventName: string;
  eventMetadata: {
    constant: string;
    eventName: string;
    tracker: string;
  };
  eventUserId: string;
  properties: Record<string, unknown>;
}

const getProductInsightApplication = (): ProductInsightApplication => {
  const userAgent = navigator.userAgent;
  const browser = detectBrowser(userAgent, navigator.vendor);
  const browserVersion = detectBrowserVersion(userAgent, navigator.vendor);
  const [os, osVersion] = detectOS(userAgent);

  return {
    ...(browser && { browser }),
    ...(browserVersion !== null && {
      'browser-version': browserVersion.toString(),
    }),
    ...(os && { os }),
    ...(osVersion && { 'os-version': osVersion }),
    'user-agent': userAgent,
    'device-type': detectDeviceType(userAgent).toLowerCase(),
  };
};

export const createProductInsightEvent = ({
  eventName,
  eventMetadata,
  eventUserId,
  properties,
}: CreateProductInsightEventParams): ProductInsightEvent => {
  return {
    header: {
      'event-id': v4(),
      'event-name': eventMetadata.eventName,
      'event-sent-timestamp': new Date().toISOString(),
      'event-user-id': eventUserId,
      'event-project': PRODUCT_INSIGHT_PROJECT,
    },
    sdk: {
      'pi-sdk': PRODUCT_INSIGHT_SDK,
      'pi-sdk-version': PRODUCT_INSIGHT_SDK_VERSION,
      'schema-version': PRODUCT_INSIGHT_SCHEMA_VERSION,
    },
    application: getProductInsightApplication(),
    body: {
      'non-pii-data': {
        'app-id': PRODUCT_INSIGHT_APP_ID,
        event: eventName,
        constant: eventMetadata.constant,
        tracker: eventMetadata.tracker,
        properties,
      },
      'pii-data': {},
    },
  };
};
